// ============================================
// P3 支付线（P-08 前端）：支付 API（经 dtwin 代理 → UBS）
// ============================================
import client from './client';

export interface PayOrder {
  orderNo: string;
  tenantId: string;
  orderType: string;
  productRef?: string | null;
  channel: string;
  amount: number;
  status: 'pending' | 'paid' | 'fulfilled' | 'approved' | 'rejected' | 'refunded' | 'cancelled';
  payUrl?: string | null;
  tradeNo?: string | null;
  expiredAt?: string | null;
  paidAt?: string | null;
  fulfilledAt?: string | null;
  createdAt?: string;
  /** F 代金券：折后金额明细（amount=实收；originalAmount=券前原价） */
  couponId?: string | null;
  couponAmount?: number;
  originalAmount?: number | null;
}

export interface PayPackage {
  id: string;
  name: string;
  price: number;
  validMonth: number;
  /** 套餐类型（free/basic/pro/enterprise/flagship），B1 弹窗按此匹配展示覆盖 */
  packageType?: string;
  featureList: string[];
  quota: Record<string, unknown>;
}

export async function fetchPayStatus(): Promise<{ enabled: boolean; mock: boolean; channels: Record<string, { ready: boolean }> }> {
  const { data } = await client.get('/pay/status');
  return data;
}

export async function fetchPayPackages(): Promise<{ items: PayPackage[] }> {
  const { data } = await client.get('/pay/packages');
  return data;
}

/** ③加购：在售加购目录 */
export interface PayAddon {
  code: string;
  name: string;
  description: string;
  price: number;
  unitLabel: string;
  durationDays: number | null;
  bonus: Record<string, unknown>;
  category: string;
}

export async function fetchPayAddons(): Promise<{ items: PayAddon[] }> {
  const { data } = await client.get('/pay/addons');
  return data;
}

/** ③加购：当前租户权益（订阅+加购聚合） */
export interface PayEntitlement {
  packageCode: string;
  paid: boolean;
  quota: {
    enable3D?: boolean;
    watermark?: boolean;
    editSessionLimit?: number;
    viewSessionLimit?: number;
    subAccountSeats?: number;
  };
  addons: {
    aiDailyBonus: number;
    smsRemaining: number;
    storageBonusGb: number;
    watermarkOff: boolean;
    enable3D: boolean;
    items: Array<{
      code: string;
      name: string;
      expireAt: string | null;
      quantity: number | null;
      used: number;
      orderNo: string;
    }>;
  };
}

export async function fetchPayEntitlement(): Promise<PayEntitlement> {
  const { data } = await client.get('/pay/entitlement');
  return data;
}

/** ⑥设备 License 档位目录 */
export interface PayLicenseTier {
  code: string;
  name: string;
  description: string;
  price: number;
  unitLabel: string;
  durationDays: number;
  features: string[];
}

export async function fetchLicenseTiers(): Promise<{ items: PayLicenseTier[] }> {
  const { data } = await client.get('/pay/license-tiers');
  return data;
}

export async function createPayOrder(body: {
  orderType: 'subscription' | 'plugin_component' | 'material' | 'addon' | 'license' | 'dtbase';
  channel: 'alipay' | 'wechat';
  packageId?: string;
  productRef?: string;
  /** ⑥License：目标设备序列号 */
  deviceSn?: string;
  /** DW-P1-4：部署项目授权（license 订单携带项目码） */
  projectCode?: string;
  amount?: number;
  subject?: string;
  /** F 代金券：用户持有的券 id（金额以服务端计算为准） */
  userCouponId?: number;
}): Promise<PayOrder> {
  // B8：基座单购走专用下单（价格服务端权威，不接受客户端金额）
  if (body.orderType === 'dtbase') {
    const { data } = await client.post('/pay/dtbase/orders', {
      assetCode: body.productRef,
      channel: body.channel,
      ...(body.userCouponId !== undefined ? { userCouponId: body.userCouponId } : {}),
    });
    return data;
  }
  const { data } = await client.post('/pay/orders', body);
  return data;
}

// ── B8：基座市场（目录 + 单购价格） ──
export interface DtbaseCatalogItem {
  assetCode: string;
  name: string;
  description: string;
  industry: string[];
  tier: string;
  access: string[];
  priceRef: string | null;
  price: number | null;
  priceTier: string | null;
  models: number;
  purchasable: boolean;
  version: string;
  updatedAt: string | null;
  /** B8-优化：基座快照图（服务端代理地址，可直接用作 img src） */
  cover?: string | null;
}

export async function fetchDtbaseCatalog(): Promise<{ items: DtbaseCatalogItem[]; tiers: Array<{ key: string; name: string; price: number; priceRef: string }> }> {
  const { data } = await client.get('/pay/dtbase/catalog');
  return data;
}

// ── F 代金券（2026-10-03） ──

export interface MyCoupon {
  id: number;
  couponId: number;
  name: string;
  couponType: 'fixed' | 'discount';
  faceValue: number;
  discountRate: number;
  maxDiscount: number;
  minAmount: number;
  status: 'available' | 'locked' | 'used' | 'expired' | 'void';
  claimAt?: string;
  expireAt?: string | null;
  orderNo?: string | null;
  usable: boolean;
  reason?: string;
  /** 试算（带 amount 请求时）：抵扣金额与折后应付 */
  discount: number;
  payable: number;
}

/** 我的优惠券（可带 orderType/amount 试算可用性） */
export async function fetchMyCoupons(params?: { orderType?: string; amount?: number }): Promise<{ items: MyCoupon[] }> {
  const { data } = await client.get('/pay/coupons/mine', {
    params: {
      orderType: params?.orderType,
      amount: params?.amount !== undefined ? params.amount : undefined,
    },
  });
  return data;
}

/** 兑换码领取 */
export async function redeemCoupon(code: string): Promise<{ userCouponId: number; couponId: number; name: string }> {
  const { data } = await client.post('/pay/coupons/redeem', { code });
  return data;
}

export async function fetchPayOrder(orderNo: string): Promise<PayOrder> {
  const { data } = await client.get(`/pay/orders/${encodeURIComponent(orderNo)}`);
  return data;
}

export async function syncPayOrder(orderNo: string): Promise<PayOrder> {
  const { data } = await client.post(`/pay/orders/${encodeURIComponent(orderNo)}/sync`);
  return data;
}

/** F：关闭待支付订单（释放占用的优惠券锁） */
export async function closePayOrder(orderNo: string): Promise<void> {
  await client.post(`/pay/orders/${encodeURIComponent(orderNo)}/close`);
}

export async function mockPayOrder(orderNo: string): Promise<PayOrder> {
  const { data } = await client.post(`/pay/orders/${encodeURIComponent(orderNo)}/mock-pay`);
  return data;
}

export async function fetchPayGrants(params?: { productType?: string; productRef?: string }): Promise<{ items: Array<{ productType: string; productRef: string; orderNo: string; status: string; expireAt?: string | null }> }> {
  const { data } = await client.get('/pay/grants', { params });
  return data;
}

/** 我的订单列表（个人中心-支付记录） */
export async function fetchPayOrders(page = 1, pageSize = 10): Promise<{ items: PayOrder[]; total: number; page: number; pageSize: number }> {
  const { data } = await client.get('/pay/orders', { params: { page, pageSize } });
  return data;
}
